import { useCallback, useEffect, useState } from 'react';

export const storageKey = (userId: string) => `ch.whatsNew.lastSeen.${userId}`;

/**
 * Per device, not per account.
 *
 * - No key yet (first run): stores `appVersion` and returns it, so nothing shows.
 * - Storage throws (private mode, blocked site data): stays `null`, so nothing shows.
 *
 * `markSeen` writes storage but leaves the returned value alone: the modal is mid-exit
 * when it's called, and changing the value would unmount it before the animation ends.
 */
export const useLastSeenVersion = (userId: string, appVersion: string) => {
  const [lastSeen, setLastSeen] = useState<string | null>(null);

  useEffect(() => {
    try {
      const key = storageKey(userId);
      const stored = window.localStorage.getItem(key);
      if (stored !== null) {
        setLastSeen(stored);
        return;
      }

      window.localStorage.setItem(key, appVersion);
      setLastSeen(appVersion);
    } catch {
      // Swallowed on purpose: with no storage we can't remember a dismissal, so the
      // modal would reappear on every visit. Showing nothing is the lesser evil.
    }
  }, [userId, appVersion]);

  const markSeen = useCallback(
    (version: string) => {
      try {
        window.localStorage.setItem(storageKey(userId), version);
      } catch {
        // Same as above: nothing to do without storage.
      }
    },
    [userId]
  );

  return { lastSeen, markSeen };
};
